/**
 * Runtime configuration helper.
 *
 * Priority:
 *  1. window.__CONFIG__  – injected by docker-entrypoint.sh at container start
 *  2. import.meta.env    – Vite build-time (used during `npm run dev`)
 *
 * This allows the SAME Docker image to run in any environment
 * (local docker-compose, CapRover, etc.) by passing different env vars.
 */

interface RuntimeConfig {
  [key: string]: string | undefined;
}

declare global {
  interface Window {
    __CONFIG__?: RuntimeConfig;
  }
}

function get(key: string, fallback?: string): string {
  // 1. Runtime config from docker-entrypoint.sh (Docker)
  const runtime = window.__CONFIG__?.[key];
  if (runtime) return runtime;

  // 2. Vite build-time env (npm run dev)
  const buildTime = (import.meta.env as Record<string, string>)[key];
  if (buildTime) return buildTime;

  // 3. Fallback
  return fallback ?? '';
}

const config = {
  // API URLs
  VITE_API_BASE_URL: get('VITE_API_BASE_URL'),
  VITE_AUTH_API_BASE_URL: get('VITE_AUTH_API_BASE_URL'),
  VITE_AUTH_FRAMEWORK_URL: get('VITE_AUTH_FRAMEWORK_URL'),
  VITE_ANALYTICS_URL: get('VITE_ANALYTICS_URL'),
  VITE_CORE_API_BASE_URL: get('VITE_CORE_API_BASE_URL'),
  VITE_AI_AGENT_API_BASE_URL: get('VITE_AI_AGENT_API_BASE_URL'),
  VITE_PUBLIC_TOOLS_API_BASE_URL: get('VITE_PUBLIC_TOOLS_API_BASE_URL'),

  // Google OAuth
  VITE_GOOGLE_CLIENT_ID: get('VITE_GOOGLE_CLIENT_ID'),
  VITE_GOOGLE_REDIRECT_URI: get('VITE_GOOGLE_REDIRECT_URI'),

  // Bing
  VITE_BING_CLIENT_ID: get('VITE_BING_CLIENT_ID'),

  // Feature flags
  VITE_USE_MOCK_DATA: get('VITE_USE_MOCK_DATA', 'false'),

  // Webtool visibility flags (set to 'false' to hide a tab)
  VITE_ENABLE_WEBTOOL_GSC: get('VITE_ENABLE_WEBTOOL_GSC', 'true'),
  VITE_ENABLE_WEBTOOL_GA4: get('VITE_ENABLE_WEBTOOL_GA4', 'true'),
  VITE_ENABLE_WEBTOOL_BING: get('VITE_ENABLE_WEBTOOL_BING', 'true'),
  VITE_ENABLE_WEBTOOL_PINTEREST: get('VITE_ENABLE_WEBTOOL_PINTEREST', 'true'),
  VITE_ENABLE_WEBTOOL_CRAWL: get('VITE_ENABLE_WEBTOOL_CRAWL', 'true'),
  VITE_AI_ENABLED_MENU: get('VITE_AI_ENABLED_MENU', 'false'),

  // Plan limits
  VITE_SITE_LIMIT_STARTER: get('VITE_SITE_LIMIT_STARTER', '1'),
  VITE_SITE_LIMIT_BASIC: get('VITE_SITE_LIMIT_BASIC', '1'),
  VITE_SITE_LIMIT_PRO: get('VITE_SITE_LIMIT_PRO', '10'),
  VITE_SITE_LIMIT_AGENCY: get('VITE_SITE_LIMIT_AGENCY', '125'),
};

export default config;
